import { useEffect, useRef } from 'react'
import { useAccount } from 'wagmi'
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react'
import { useTronWallet } from '@/hooks/useTronWallet'
import { referralApi } from '@/services/api'

/**
 * Posts wallet connection to /api/wallet/connect when a wallet connects.
 *
 * Always fires on connect (creates wallet row for sanctions screening).
 * Sends ref from localStorage for last-touch attribution — referred_by
 * is overwritten each time the user connects with a new referral link.
 *
 * Covers all three VM families the app connects: EVM, Solana and Tron. The Tron
 * branch is #363 step 3 — PR #401 taught the backend to classify Tron correctly
 * (classifyChainType -> 'tron', address-validator.ts:114-118) but nothing ever sent
 * it one, so a Tron user could connect, browse and leave without a wallets row,
 * undercounting #333's funnel and resolving every Tron order to country 'UNKNOWN'.
 *
 * Deduplicates via useRef<Set> so each address is only sent once per session. The Set
 * is keyed BY VALUE (`<family>:<address>`), so wallet-adapter identity churn across
 * re-renders cannot cause a repeat POST. It is per-mount on purpose: a page reload
 * re-posts, and the backend appends to connection_history (capped at 100) so a
 * returning connect refreshes its geo.
 */
export function useReferralAttribution() {
  const { address: evmAddress, isConnected: isEvmConnected } = useAccount()
  const { publicKey: solanaPublicKey, connected: isSolanaConnected } = useSolanaWallet()
  const solanaAddress = solanaPublicKey?.toBase58()
  const { tronAddress, tronConnected } = useTronWallet()
  const sentRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!isEvmConnected || !evmAddress) return

    const key = `evm:${evmAddress}`
    if (sentRef.current.has(key)) return
    sentRef.current.add(key)

    // Read ref from localStorage (set by useReferralCapture on page visit)
    const ref = localStorage.getItem('parsebridge:ref') || undefined

    referralApi.walletConnect({
      address: evmAddress,
      ref,
    }).catch(console.warn)
  }, [isEvmConnected, evmAddress])

  useEffect(() => {
    if (!isSolanaConnected || !solanaAddress) return

    const key = `sol:${solanaAddress}`
    if (sentRef.current.has(key)) return
    sentRef.current.add(key)

    const ref = localStorage.getItem('parsebridge:ref') || undefined

    referralApi.walletConnect({
      address: solanaAddress,
      ref,
    }).catch(console.warn)
  }, [isSolanaConnected, solanaAddress])

  useEffect(() => {
    if (!tronConnected || !tronAddress) return

    const key = `tron:${tronAddress}`
    if (sentRef.current.has(key)) return
    sentRef.current.add(key)

    const ref = localStorage.getItem('parsebridge:ref') || undefined

    // `tronAddress` is passed BYTE-FOR-BYTE. Tron base58check is case-SENSITIVE, so a
    // lowercased Tron address is a DIFFERENT, invalid address — normalising here would
    // re-break #363, which the backend fixed by exempting Tron from lowercasing on the
    // shape guard (normalizeAddressForStorage, address-validator.ts:156-160). No
    // .toLowerCase(), no trim, no normalisation on this path.
    referralApi.walletConnect({
      address: tronAddress,
      ref,
    }).catch(console.warn)
  }, [tronConnected, tronAddress])
}
